import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';
import ImageUpload from '../../components/admin/ImageUpload';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDeftFeed = () => {
  const { getAuthHeaders } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/deft-feed`, getAuthHeaders());
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching feed items:', error);
    }
    setLoading(false);
  };

  const handleEdit = (item) => {
    setEditing(item.id);
    setFormData(item);
  };

  const handleSave = async () => {
    try {
      const dataToSave = { ...formData };
      if (!dataToSave.id || dataToSave.id === 'new') {
        dataToSave.id = crypto.randomUUID();
      }
      
      if (editing === 'new') {
        await axios.post(`${API}/admin/deft-feed`, dataToSave, getAuthHeaders());
      } else {
        await axios.put(`${API}/admin/deft-feed/${formData.id}`, dataToSave, getAuthHeaders());
      }
      await fetchItems();
      setEditing(null);
      setFormData({});
      alert('Feed item saved successfully!');
    } catch (error) {
      console.error('Error saving feed item:', error);
      alert('Error saving feed item: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this feed item?')) {
      try {
        await axios.delete(`${API}/admin/deft-feed/${id}`, getAuthHeaders());
        await fetchItems();
        alert('Feed item deleted successfully!');
      } catch (error) {
        console.error('Error deleting feed item:', error);
        alert('Error deleting feed item');
      }
    }
  };

  const handleNew = () => {
    setEditing('new');
    setFormData({
      id: '',
      title: '',
      excerpt: '',
      image: '',
      link: '#'
    });
  };

  if (loading) return <AdminLayout><p className="text-white">Loading...</p></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="display-medium text-white">Nerd Feed Management</h1>
          <button onClick={handleNew} className="btn-primary flex items-center gap-2">
            <Plus size={20} /> Add New Item
          </button>
        </div>

        {editing && (
          <div className="bg-white/10 border border-white/25 p-6 rounded-lg">
            <h2 className="heading-2 text-white mb-4">{editing === 'new' ? 'New Feed Item' : 'Edit Feed Item'}</h2>
            <div className="space-y-4">
              <div>
                <label className="body-small text-white mb-1 block">Title</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                  placeholder="e.g., Psychology: Flow States"
                />
              </div>
              <div>
                <label className="body-small text-white mb-1 block">Excerpt</label>
                <textarea
                  value={formData.excerpt || ''}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded h-24"
                  placeholder="Short description (50 words max)"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <ImageUpload
                    label="Image"
                    value={formData.image}
                    onChange={(v) => setFormData({ ...formData, image: v })}
                    testId="feed-image"
                  />
                </div>
                <div>
                  <label className="body-small text-white mb-1 block">Link URL</label>
                  <input
                    type="text"
                    value={formData.link || ''}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="https:// or #"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                <Save size={20} /> Save
              </button>
              <button onClick={() => { setEditing(null); setFormData({}); }} className="btn-secondary flex items-center gap-2">
                <X size={20} /> Cancel
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div key={item.id} className="bg-white/5 border border-white/25 p-6 rounded-lg">
              <div className="flex justify-between items-start mb-4">
                <h3 className="heading-3 text-white flex-1">{item.title}</h3>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(item)} className="text-[#00FFD1] hover:text-[#6FD2C0]">
                    <Edit size={20} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-400">
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
              <p className="body-small text-white/85">{item.excerpt}</p>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDeftFeed;
